/**
 * One JSON-LD node.
 *
 * `dangerouslySetInnerHTML` is unavoidable here: a <script> element's contents
 * are not parsed as JSX text, so React will not render structured data any
 * other way. What has changed is where the data comes from. It used to be
 * generated only from typed modules in the repository; now a title, a caption
 * or a description can come from the CMS, so the serialised JSON is escaped
 * before it is written.
 *
 * `<` becomes its unicode escape, which JSON.parse reads back as `<` and an
 * HTML parser never sees as the start of a tag. Without it, a caption
 * containing `</script>` would close this element early and put the rest of the
 * node into the document as markup.
 *
 * U+2028 and U+2029 get the same treatment. They are valid inside a JSON string
 * and are line terminators in JavaScript source, so one pasted into a caption
 * would break the parse of a script the page has already committed to.
 */
export function JsonLd({ data }: { data: Record<string, unknown> | Record<string, unknown>[] | null }) {
  if (!data) return null;

  const json = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}
